import { useState } from 'react'
import { Ban } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { useMovePopoverContainer } from './popoverContainer'

/**
 * Leave one occupant out of the plan, with the reason the server requires
 * (z8nrz7f2gh). Asked for in place, next to the row, rather than through
 * `window.prompt` — a browser can suppress a prompt, and then the button
 * simply did nothing.
 */
export function ExcludeMoveButton({
  name,
  disabled,
  onExclude,
}: {
  /** How the move names itself: the item, then where it is. */
  name: string
  disabled?: boolean
  onExclude: (reason: string) => void
}) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const container = useMovePopoverContainer()

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) setReason('')
      }}
    >
      <PopoverTrigger asChild>
        <Button size='sm' variant='outline' className='h-7 gap-1 text-muted-foreground' disabled={disabled}>
          <Ban className='h-3.5 w-3.5' />
          Exclude
        </Button>
      </PopoverTrigger>
      {/* Portalled into the Replace dialog itself: outside it the focus trap
          left the box unclickable, and in place the sticky Actions cells of
          the rows below painted over it (z8nrz7f2gh). */}
      <PopoverContent container={container} align='end' className='w-80 space-y-2 p-3'>
        <p className='text-sm font-medium'>Leave this item out of the plan?</p>
        <p className='text-xs text-muted-foreground'>{name}</p>
        <Textarea
          autoFocus
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder='Why it is being left behind (required)'
          aria-label='Reason for excluding'
          className='text-sm'
        />
        <div className='flex justify-end gap-1.5'>
          <Button size='sm' variant='ghost' onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            size='sm'
            disabled={!reason.trim()}
            onClick={() => {
              onExclude(reason.trim())
              setOpen(false)
              setReason('')
            }}
          >
            Exclude
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
